import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Star, Shield, Users, ChevronRight, Car, ArrowUp, Phone, User, LogOut } from 'lucide-react';
import { services, fleet } from '../mockData';
import BookingSection from '../components/BookingSection';
import FleetSection from '../components/FleetSection';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Footer from '../components/Footer';
import { useLanguage } from '../LanguageContext';
import { toast } from 'sonner';

const Home = () => {
  const navigate = useNavigate();
  const [showBooking, setShowBooking] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const { t } = useLanguage();

  // Check if customer is logged in
  useEffect(() => {
    const token = localStorage.getItem('customerToken');
    const customerData = localStorage.getItem('customerData');
    
    if (token && customerData) {
      setIsLoggedIn(true);
      try {
        const data = JSON.parse(customerData);
        setCustomerName(data.name || 'Client');
      } catch (e) {
        console.error('Error parsing customer data:', e);
      }
    }
  }, []);

  // Show/hide scroll to top button based on scroll position
  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToBooking = () => {
    setShowBooking(true);
    setTimeout(() => {
      document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogout = () => {
    localStorage.removeItem('customerToken');
    localStorage.removeItem('customerData');
    setIsLoggedIn(false);
    setCustomerName('');
    toast.success('Déconnexion réussie');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      <Helmet>
        <title>Luxury Transfer Service - Service de Chauffeur Premium en Belgique | Bruxelles</title>
        <meta name="description" content="Service de transport de luxe en Belgique. Chauffeur professionnel pour transferts aéroport, événements d'entreprise et locations horaires. Lexus ES300h et Mercedes E-Class. Réservation en ligne 24/7." />
        <link rel="canonical" href="https://www.luxurytransferservices.be/" />
      </Helmet>
      
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-md border-b border-gold/20">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity">
              <div className="w-12 h-12 flex items-center justify-center">
                <img 
                  src="/logo.png" 
                  alt="Luxury Transfer Service LTX" 
                  className="w-12 h-12 object-contain"
                />
              </div>
              <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-gold to-amber-400 bg-clip-text text-transparent">
                LTX
              </span>
            </a>
            <div className="flex items-center space-x-4">
              <a 
                href="tel:+3223150254" 
                className="flex items-center gap-2 px-3 py-2 border border-gold/30 rounded-full text-gold hover:bg-gold/10 transition-all"
              >
                <Phone className="w-4 h-4" />
                <span className="font-semibold text-sm md:text-base">+32 2 315 02 54</span>
              </a>
              <LanguageSwitcher />
              <nav className="hidden md:flex items-center space-x-6">
                <a href="#services" className="text-gray-300 hover:text-gold transition-colors">{t('header.services')}</a>
                <a href="#fleet" className="text-gray-300 hover:text-gold transition-colors">{t('header.fleet')}</a>
                <a href="#about" className="text-gray-300 hover:text-gold transition-colors">{t('header.about')}</a>
                
                {/* Auth Buttons */}
                {isLoggedIn ? (
                  <>
                    <button 
                      onClick={() => navigate('/client/dashboard')} 
                      className="px-3 py-1.5 border border-amber-500/50 text-amber-400 rounded-full hover:bg-amber-500/10 transition-all font-semibold flex items-center gap-1.5 text-sm"
                    >
                      <User className="w-3 h-3" />
                      {customerName}
                    </button>
                    <button 
                      onClick={handleLogout} 
                      className="px-3 py-1.5 border border-red-500/50 text-red-400 rounded-full hover:bg-red-500/10 transition-all font-semibold flex items-center gap-1.5 text-sm"
                    >
                      <LogOut className="w-3 h-3" />
                      Déconnexion
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      onClick={() => navigate('/client/login')} 
                      className="px-3 py-1.5 border border-amber-500/50 text-amber-400 rounded-full hover:bg-amber-500/10 transition-all font-semibold flex items-center gap-1.5 text-sm"
                    >
                      <User className="w-3 h-3" />
                      Connexion
                    </button>
                    <button 
                      onClick={() => navigate('/client/signup')} 
                      className="px-3 py-1.5 border border-purple-500/50 text-purple-400 rounded-full hover:bg-purple-500/10 transition-all font-semibold flex items-center gap-1.5 text-sm"
                    >
                      <Star className="w-3 h-3" />
                      Inscription
                    </button>
                  </>
                )}
                
                <Button onClick={scrollToBooking} className="bg-gradient-to-r from-gold to-amber-600 text-black hover:from-amber-600 hover:to-gold">
                  {t('header.bookNow')}
                </Button>
              </nav>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1708019931289-1c954376c911?crop=entropy&cs=srgb&fm=jpg&q=85" 
            alt="Luxury vehicle"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-bold leading-tight">
              <span className="text-white">{t('hero.title')}</span>
              <br />
              <span className="bg-gradient-to-r from-gold via-amber-400 to-gold bg-clip-text text-transparent">
                {t('hero.titleHighlight')}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-2xl mx-auto">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <Button 
                size="lg" 
                onClick={scrollToBooking}
                className="bg-gradient-to-r from-gold to-amber-600 text-black hover:from-amber-600 hover:to-gold text-lg px-8 py-6 shadow-lg shadow-gold/20"
              >
                {t('hero.reserveRide')}
                <ChevronRight className="ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => document.getElementById('fleet')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-gold text-gold hover:bg-gold hover:text-black text-lg px-8 py-6"
              >
                {t('hero.viewFleet')}
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gold/50 rounded-full flex justify-center p-2">
            <div className="w-1 h-3 bg-gold rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-radial from-gold/5 to-transparent"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{t('services.title')}</h2>
            <p className="text-xl text-gray-400">{t('services.subtitle')}</p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <Card 
              className="bg-gradient-to-br from-gray-900 to-black border-gold/20 hover:border-gold/50 transition-all duration-300 hover:shadow-xl hover:shadow-gold/10 group"
            >
              <CardHeader>
                <div className="w-14 h-14 bg-gradient-to-br from-gold to-amber-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform mx-auto">
                  <Car className="w-7 h-7 text-black" />
                </div>
                <CardTitle className="text-3xl text-white text-center">{t('services.transfer.title')}</CardTitle>
                <CardDescription className="text-gray-400 text-lg text-center">
                  {t('services.transfer.description')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-white font-semibold mb-3 text-lg">{t('services.pricing.title')}</h4>
                    <ul className="space-y-2">
                      <li className="flex items-center text-gray-300">
                        <div className="w-1.5 h-1.5 bg-gold rounded-full mr-3"></div>
                        {t('services.pricing.basePrice')}: 8€
                      </li>
                      <li className="flex items-center text-gray-300">
                        <div className="w-1.5 h-1.5 bg-gold rounded-full mr-3"></div>
                        {t('services.pricing.perKm')}: 1.50€
                      </li>
                      <li className="flex items-center text-gray-300">
                        <div className="w-1.5 h-1.5 bg-gold rounded-full mr-3"></div>
                        {t('services.pricing.perMinute')}: 0.40€
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-3 text-lg">{t('services.included.title')}</h4>
                    <ul className="space-y-2">
                      <li className="flex items-center text-gray-300">
                        <div className="w-1.5 h-1.5 bg-gold rounded-full mr-3"></div>
                        {t('services.included.driver')}
                      </li>
                      <li className="flex items-center text-gray-300">
                        <div className="w-1.5 h-1.5 bg-gold rounded-full mr-3"></div>
                        {t('services.included.vehicles')}
                      </li>
                      <li className="flex items-center text-gray-300">
                        <div className="w-1.5 h-1.5 bg-gold rounded-full mr-3"></div>
                        {t('services.included.service247')}
                      </li>
                      <li className="flex items-center text-gray-300">
                        <div className="w-1.5 h-1.5 bg-gold rounded-full mr-3"></div>
                        {t('services.included.wifi')}
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-gold/10 border border-gold/30 rounded-lg">
                  <p className="text-center text-gray-300">
                    <span className="text-gold font-semibold">{t('services.example.label')}:</span> {t('services.example.route')} (15km, 25min) = <span className="text-white font-bold">40.50€</span>
                  </p>
                  <p className="text-center text-xs text-gray-400 mt-1">
                    {t('services.example.calculation')}: 8€ + (15km × 1.50€) + (25min × 0.40€)
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Fleet Section */}
      <FleetSection />

      {/* Why Choose Us */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{t('whyUs.title')}</h2>
            <p className="text-xl text-gray-400">{t('whyUs.subtitle')}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-gold/20 to-amber-600/20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform border-2 border-gold/30">
                <Shield className="w-10 h-10 text-gold" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">{t('whyUs.licensed.title')}</h3>
              <p className="text-gray-400">{t('whyUs.licensed.description')}</p>
            </div>
            
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-gold/20 to-amber-600/20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform border-2 border-gold/30">
                <Users className="w-10 h-10 text-gold" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">{t('whyUs.chauffeurs.title')}</h3>
              <p className="text-gray-400">{t('whyUs.chauffeurs.description')}</p>
            </div>
            
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-gold/20 to-amber-600/20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform border-2 border-gold/30">
                <Star className="w-10 h-10 text-gold" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">{t('whyUs.fleet.title')}</h3>
              <p className="text-gray-400">{t('whyUs.fleet.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 bg-black relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-radial from-gold/5 to-transparent"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{t('about.title')}</h2>
            <p className="text-xl text-gray-400">{t('about.subtitle')}</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Experience */}
            <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-gold/30 hover:border-gold transition-all duration-300">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-gold/20 to-amber-600/20 rounded-full flex items-center justify-center mb-4 border-2 border-gold/30">
                  <Star className="w-8 h-8 text-gold" />
                </div>
                <CardTitle className="text-2xl text-white">{t('about.experience.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 leading-relaxed">{t('about.experience.description')}</p>
              </CardContent>
            </Card>

            {/* Commitment */}
            <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-gold/30 hover:border-gold transition-all duration-300">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-gold/20 to-amber-600/20 rounded-full flex items-center justify-center mb-4 border-2 border-gold/30">
                  <Shield className="w-8 h-8 text-gold" />
                </div>
                <CardTitle className="text-2xl text-white">{t('about.commitment.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 leading-relaxed">{t('about.commitment.description')}</p>
              </CardContent>
            </Card>

            {/* Values */}
            <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-gold/30 hover:border-gold transition-all duration-300">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-gold/20 to-amber-600/20 rounded-full flex items-center justify-center mb-4 border-2 border-gold/30">
                  <Star className="w-8 h-8 text-gold" fill="currentColor" />
                </div>
                <CardTitle className="text-2xl text-white">{t('about.values.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 leading-relaxed">{t('about.values.description')}</p>
              </CardContent>
            </Card>

            {/* Team */}
            <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-gold/30 hover:border-gold transition-all duration-300">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-gold/20 to-amber-600/20 rounded-full flex items-center justify-center mb-4 border-2 border-gold/30">
                  <Users className="w-8 h-8 text-gold" />
                </div>
                <CardTitle className="text-2xl text-white">{t('about.team.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 leading-relaxed">{t('about.team.description')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Booking Section */}
      {showBooking && <BookingSection />}

      {/* Footer */}
      <Footer />

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 bg-gradient-to-r from-gold to-amber-600 text-black p-4 rounded-full shadow-lg hover:from-amber-600 hover:to-gold transition-all transform hover:scale-110"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}

      {/* Paiement Sécurisé Section - Mobile Optimized */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyMTgsIDE2NSwgMzIsIDAuMSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8 md:mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-blue-500/10 border border-blue-500/30 rounded-full mb-3 md:mb-4">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <span className="text-blue-400 font-medium text-xs md:text-sm">Paiement 100% Sécurisé</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 md:mb-4 px-4">
                Payez en toute <span className="text-gold">sécurité</span>
              </h2>
              <p className="text-gray-400 text-sm md:text-lg max-w-2xl mx-auto px-4">
                Paiements en ligne sécurisés via Stripe ou en espèces à bord.
              </p>
            </div>

            {/* Payment Options - Mobile Optimized */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mb-8 md:mb-12 px-2">
              {/* Online Payment */}
              <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/30 rounded-xl md:rounded-2xl p-4 md:p-8 hover:border-blue-500/50 transition-all">
                <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-500/20 rounded-lg md:rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 md:w-6 md:h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-white">Paiement en ligne</h3>
                </div>
                
                <ul className="space-y-2 md:space-y-3 mb-4 md:mb-6">
                  <li className="flex items-start gap-2 md:gap-3 text-gray-300 text-sm md:text-base">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Sécurisé par <strong className="text-white">Stripe</strong></span>
                  </li>
                  <li className="flex items-start gap-2 md:gap-3 text-gray-300 text-sm md:text-base">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Protection SSL & 3D Secure</span>
                  </li>
                  <li className="flex items-start gap-2 md:gap-3 text-gray-300 text-sm md:text-base">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Lien envoyé par email</span>
                  </li>
                  <li className="flex items-start gap-2 md:gap-3 text-gray-300 text-sm md:text-base">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Toutes cartes acceptées</span>
                  </li>
                </ul>

                <a 
                  href="/paiement-en-ligne-securise"
                  className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium transition-colors text-sm md:text-base"
                >
                  En savoir plus
                  <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>

              {/* Cash Payment */}
              <div className="bg-gradient-to-br from-amber-500/10 to-orange-600/5 border border-amber-500/30 rounded-xl md:rounded-2xl p-4 md:p-8 hover:border-amber-500/50 transition-all">
                <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-amber-500/20 rounded-lg md:rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 md:w-6 md:h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-white">Paiement en espèces</h3>
                </div>
                
                <ul className="space-y-2 md:space-y-3 mb-4 md:mb-6">
                  <li className="flex items-start gap-2 md:gap-3 text-gray-300 text-sm md:text-base">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Payez au chauffeur</span>
                  </li>
                  <li className="flex items-start gap-2 md:gap-3 text-gray-300 text-sm md:text-base">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Espèces (EUR)</span>
                  </li>
                  <li className="flex items-start gap-2 md:gap-3 text-gray-300 text-sm md:text-base">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Simple et rapide</span>
                  </li>
                  <li className="flex items-start gap-2 md:gap-3 text-gray-300 text-sm md:text-base">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Reçu à la demande</span>
                  </li>
                </ul>

                <div className="text-amber-400 font-medium text-sm md:text-base">
                  Choisissez lors de la réservation
                </div>
              </div>
            </div>

            {/* Trust Badges - Mobile Optimized */}
            <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4 sm:gap-6 md:gap-8 pt-6 md:pt-8 border-t border-gray-800 px-4">
              <div className="flex items-center gap-2 md:gap-3">
                <svg className="w-8 h-8 md:w-10 md:h-10 text-blue-500 flex-shrink-0" viewBox="0 0 28 28" fill="currentColor">
                  <path d="M13.976 0C6.259 0 0 6.261 0 13.98c0 7.72 6.259 13.98 13.976 13.98 7.717 0 13.976-6.26 13.976-13.98C27.952 6.261 21.693 0 13.976 0zm0 25.523c-6.364 0-11.543-5.181-11.543-11.543 0-6.362 5.179-11.543 11.543-11.543 6.364 0 11.543 5.181 11.543 11.543 0 6.362-5.179 11.543-11.543 11.543z"/>
                  <path d="M11.285 17.711l-3.459-3.461a1.216 1.216 0 00-1.722 1.721l4.32 4.322a1.216 1.216 0 001.722 0l8.641-8.642a1.216 1.216 0 00-1.721-1.721l-7.78 7.781z"/>
                </svg>
                <div>
                  <div className="text-white font-bold text-sm md:text-base">Stripe Verified</div>
                  <div className="text-gray-400 text-xs md:text-sm">Paiements sécurisés</div>
                </div>
              </div>

              <div className="flex items-center gap-2 md:gap-3">
                <svg className="w-8 h-8 md:w-10 md:h-10 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <div className="text-white font-bold text-sm md:text-base">SSL Sécurisé</div>
                  <div className="text-gray-400 text-xs md:text-sm">Données cryptées</div>
                </div>
              </div>

              <div className="flex items-center gap-2 md:gap-3">
                <svg className="w-8 h-8 md:w-10 md:h-10 text-gold flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <div>
                  <div className="text-white font-bold text-sm md:text-base">3D Secure</div>
                  <div className="text-gray-400 text-xs md:text-sm">Protection maximale</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
